import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import classnames from 'classnames'
import { v4 as uuidv4 } from 'uuid'
import some from 'lodash/some'
import sanitizeUrlsString from '@Utils/SanitizeUrlsString'
import modifyUrls from '@Utils/ModifyUrls'
import validateUrls from '@Utils/ValidateUrls'
import YouTube from '@Images/upload_youtube.svg'
import Instagram from '@Images/upload_instagram.svg'
import LinkedIn from '@Images/upload_linkedin.svg'
import Alert from '@Images/project_setup_form_alert.svg'

import {
  IElementForProjectVersion,
} from '@SiteModules/ProjectVersionSettings/types'

import {
  resetSizeLimitsAlertAtSubmitUrl,
} from '@SiteModules/ProjectVersionSettings/actions'

import {
  getShowSizeLimitsAlertAtSubmitUrl,
} from '@SiteModules/ProjectVersionSettings/selectors'

import {
  submitUrls,
} from '@SiteModules/Element/actions'

const GOOGLE_DRIVE_URL_PATTERNS = ['docs.google.com', 'drive.google.com']

interface SubmitUrlProps {
  readonly currentWorkspaceId: number
  readonly maxFileSizeInGB: string
  readonly elementsForProjectVersions: IElementForProjectVersion[]
  readonly maxNumberOfFilesPerSetup: number
  readonly hasFilesOrSubmittingUrls: boolean
  readonly showMissingFileAlert: boolean
  readonly showSubmitUrlInput: boolean
  setHasStartedFirstUploadOrSubmit(newSetHasStartedFirstUploadOrSubmit: boolean): void
  setShowMissingFileAlert(newShowMissingFileAlert: boolean): void
  renderLimitsAlert(limitsAlert: string, resetLimitsAlert: () => void): JSX.Element
}

const SubmitUrl: React.FC<SubmitUrlProps> = props => {
  const dispatch = useDispatch()

  const {
    currentWorkspaceId,
    maxFileSizeInGB,
    elementsForProjectVersions,
    maxNumberOfFilesPerSetup,
    hasFilesOrSubmittingUrls,
    showMissingFileAlert,
    showSubmitUrlInput,
  } = props

  const showSizeLimitsAlertAtSubmitUrl = useSelector(getShowSizeLimitsAlertAtSubmitUrl)

  const [submitUrlInputIsFocused, setSubmitUrlInputIsFocused] = useState(false)
  const [url, setUrl] = useState('')
  const [limitsSubmitAlert, setLimitsSubmitAlert] = useState<string | null>(null)
  const [urlAlert, setUrlAlert] = useState<string | null>(null)

  const urlIsEmpty = url.length === 0
  const showLimitsSubmitAlert = !!limitsSubmitAlert
  const showUrlAlert = !!urlAlert

  useEffect(() => {
    if (showSizeLimitsAlertAtSubmitUrl) {
      setLimitsSubmitAlert(`The file exceeds the ${maxFileSizeInGB} GB size limit.`)
    }
  }, [showSizeLimitsAlertAtSubmitUrl])

  const resetLimitsSubmitAlert = () => {
    setLimitsSubmitAlert(null)

    if (showSizeLimitsAlertAtSubmitUrl) {
      dispatch(resetSizeLimitsAlertAtSubmitUrl())
    }
  }

  const handleSetUrl = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUrlAlert(null)
    const newUrl = (e.target as HTMLInputElement).value
    setUrl(newUrl)
  }

  const handleSetFocus = () => {
    setSubmitUrlInputIsFocused(true)
  }

  const handleRemoveFocus = () => {
    setSubmitUrlInputIsFocused(false)
  }

  const handleKeydown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !urlIsEmpty && submitUrlInputIsFocused) {
      handleSubmitUrl()
    }
  }

  const handleSubmitUrl = () => {
    if (elementsForProjectVersions.length + 1 > maxNumberOfFilesPerSetup) {
      setLimitsSubmitAlert(`You can upload a maximum of ${maxNumberOfFilesPerSetup} files.`)
    } else {
      const modifiedAndSanitizedUrl = modifyUrls(sanitizeUrlsString(url))[0]

      if (!validateUrls([url]) || !modifiedAndSanitizedUrl) {
        setUrlAlert('The URL is invalid. Please check and try again.')
      } else if (some(GOOGLE_DRIVE_URL_PATTERNS, pattern => modifiedAndSanitizedUrl.includes(pattern))) {
        setUrlAlert('Google Drive links are not supported. Please upload files directly.')
      } else {
        dispatch(submitUrls({ urls: [modifiedAndSanitizedUrl], workspaceId: currentWorkspaceId, submitUrlIdentifier: uuidv4() }))
        setUrl('')
        props.setHasStartedFirstUploadOrSubmit(true)
        props.setShowMissingFileAlert(false)
      }
    }
  }

  const renderSubmitUrlText = () => {
    return (
      <div className="UploadAndImport__text UploadAndImport__text-url">
        {hasFilesOrSubmittingUrls ? 'Add a file from a URL:' : 'Or import from a URL:'}
      </div>
    )
  }

  const renderIcons = () => {
    return (
      <div className="UploadAndImport__submit-url-input-icons">
        <YouTube />
        <Instagram />
        <LinkedIn />
      </div>
    )
  }

  const renderConfirm = () => {
    return (
      <div
        className="UploadAndImport__submit-url-input-confirm"
        onClick={handleSubmitUrl}
      >
        confirm
      </div>
    )
  }

  const renderSubmitUrlInput = () => {
    return (
      <div
        className={classnames(
          'UploadAndImport__submit-url-input-wrapper',
          { 'UploadAndImport__submit-url-input-wrapper-focused': submitUrlInputIsFocused },
          { 'UploadAndImport__submit-url-input-wrapper-alert': (showMissingFileAlert || showUrlAlert || showLimitsSubmitAlert) && !submitUrlInputIsFocused }
        )}
      >
        <input
          id="upload-and-import-submit-url-input"
          className="UploadAndImport__submit-url-input"
          placeholder="Paste URL here..."
          onFocus={handleSetFocus}
          onBlur={handleRemoveFocus}
          autoComplete="off"
          value={url}
          onChange={handleSetUrl}
          onKeyDown={handleKeydown}
        />
        {urlIsEmpty && renderIcons()}
        {!urlIsEmpty && renderConfirm()}
      </div>
    )
  }

  const renderMissingFileAlert = () => {
    return (
      <div className="SetupProjectPopup__alert">
        <Alert />
        No video found. Please upload a video to create your project.
      </div>
    )
  }

  const renderUrlAlert = () => {
    return (
      <div className="SetupProjectPopup__alert">
        <Alert />
        {urlAlert}
      </div>
    )
  }

  return (
    <React.Fragment>
      {showSubmitUrlInput && renderSubmitUrlText()}
      {showSubmitUrlInput && renderSubmitUrlInput()}
      {showUrlAlert && renderUrlAlert()}
      {showLimitsSubmitAlert && props.renderLimitsAlert(limitsSubmitAlert, resetLimitsSubmitAlert)}
      {showMissingFileAlert && renderMissingFileAlert()}
    </React.Fragment>
  )
}

export default SubmitUrl
